// file: src/components/viewers/WordDocViewer.jsx
import React, { useState, useEffect } from 'react';
import mammoth from 'mammoth';
import { useFileDataCache } from '../../hooks/useFileDataCache';
import styles from './WordDocViewer.module.css';

/**
 * Renders a Word file (docx) using mammoth.js to convert to HTML
 */
function WordDocViewer({ file }) {
  const { fileData, loading, error } = useFileDataCache(file.id);
  const [htmlContent, setHtmlContent] = useState('');
  const [conversionError, setConversionError] = useState(null);

  // Convert DOCX to HTML when file data is available
  useEffect(() => {
    if (!fileData) return;

    const processDocument = async () => {
      try {
        // Configure mammoth with options for better Word-like styling
        const options = {
          styleMap: [
            "p[style-name='Heading 1'] => h1:fresh",
            "p[style-name='Heading 2'] => h2:fresh",
            "p[style-name='Heading 3'] => h3:fresh",
            "p[style-name='Title'] => h1.document-title:fresh",
            "r[style-name='Strong'] => strong",
            "p[style-name='List Paragraph'] => li:fresh"
          ]
        };

        // Convert the array buffer to HTML with options
        const result = await mammoth.convertToHtml({ 
          arrayBuffer: fileData 
        }, options);
        
        setHtmlContent(result.value);
        
        // Log any warnings
        if (result.messages.length > 0) {
          console.warn('Mammoth conversion warnings:', result.messages);
        }
      } catch (err) {
        console.error('Error converting document:', err);
        setConversionError('Failed to convert document. The file may be corrupted or in an unsupported format.');
      }
    };

    processDocument();
  }, [fileData]);

  if (loading) {
    return (
      <div className={styles.infoMessage}>
        <div className={styles.loadingSpinner} />
        Loading document...
      </div>
    );
  }

  if (error) {
    return <div className={styles.errorMessage}>Failed to load document: {error.message}</div>;
  }

  if (conversionError) {
    return <div className={styles.errorMessage}>{conversionError}</div>;
  }

  if (!htmlContent) {
    return (
      <div className={styles.infoMessage}>
        <div className={styles.loadingSpinner} />
        Converting document...
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.docHeader}>
        <h2 className={styles.docTitle}>{file.name}</h2>
      </div>
      <div className={styles.documentContent}>
        <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
      </div>
    </div>
  );
}

export default WordDocViewer;


